import { FC, useMemo, useState } from 'react';
import cx from 'classnames';
import { ChainsDictionary } from '@/config';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import { Popover } from 'antd';
import { useCopyToClipboard } from '@/hooks';
import { getIconByNetwork } from '@/helpers';
import { CryptoLabelPill } from '@/components';
import { SvgIcon } from '@/components/SvgIcon';

import styles from './AnalyzeWallet.module.sass';

interface AnalyzeWalletProps {
  incomeConnected?: boolean;
  outcomeConnected?: boolean;
  address: string;
  network: string;
  className?: string;
  counterparty?: any;
}

const AnalyzeWallet: FC<AnalyzeWalletProps> = ({
  incomeConnected = false,
  outcomeConnected = false,
  address,
  network,
  className,
  counterparty,
}) => {
  const { t } = useTranslation();
  const [, copy] = useCopyToClipboard();

  const classes = cx(
    styles.wallet,
    {
      [styles['wallet--income']]: incomeConnected,
      [styles['wallet--outcome']]: outcomeConnected,
    },
    className,
  );

  const clusterName = useMemo(() => {
    if (counterparty?.id) {
      return counterparty.name || counterparty.slug || '';
    }
    return '';
  }, []);

  const clusterNote = parse(
    t('translation.named_cluster_title', {
      cluster: clusterName || 'unknown cluster',
    }).replaceAll('\n', '<br />'),
  );

  const networkIcon = getIconByNetwork(network || '');
  const networkName = ChainsDictionary[network || '']?.name || network;
  const assetSymbol = '';

  return (
    <div className={classes}>
      <div className={styles.inner}>
        <div className={styles.address}>
          <CryptoLabelPill
            iconUrl={networkIcon}
            label={networkName || ''}
            abbr={assetSymbol || ''}
            className={styles.labelPill}
          />
          {address}
        </div>
        {counterparty?.id && (
          <Popover trigger="hover" content={clusterNote}>
            <div className={styles.cluster}>
              <span className={styles.cluster_name}>
                {clusterName || 'Unnamed cluster'}
                <sup>?</sup>
              </span>
            </div>
          </Popover>
        )}

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.button}
            onClick={() => copy(address)}
          >
            <SvgIcon name="copy" />
            <span>{t('translation.copy_address')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalyzeWallet;
